import ICustomer from "@/module/main/dto/financing-v3/ICustomer";
import Customer from "@/module/main/dto/financing-v3/Customer";
import IModel from "@/module/main/model/applicationForm/IApplicationForm";
import ClientType from "@/module/main/dto/ClientTypeEnum";
import IPerson from "@/module/main/dto/financing-v3/IPerson";
import ICompany from "@/module/main/dto/financing-v3/ICompany";
import EnumUtil from "@/app/util/Enum";
import Cohabitation from "@/module/main/dto/customer/CohabitationEnum";
import Education from "@/module/main/dto/customer/EducationEnum";
import HousingTenure from "@/module/main/dto/customer/HousingTenureEnum";
import Person from "@/module/main/dto/financing-v3/Person";
import ICustomerModel from "@/module/main/model/applicationForm/ICustomer";
import CustomerBaseMapper from "@/module/main/service/CustomerBaseMapper";
import CurrencyCode from "@/module/main/dto/CurrencyCodeEnum";
import IEntrepreneur from "@/module/main/model/applicationForm/IEntrepreneur";
import Company from "@/module/main/dto/financing-v3/Company";
import IAddressFlags from "@/module/main/dto/customer/IAddressFlags";
import AddressFlagEnum from "@/module/main/dto/customer/AddressFlagEnum";
import OrderType from "@/module/main/dto/OrderTypeEnum";
import FinancedSubjectType from "@/module/main/dto/FinancedSubjectTypeEnum";
import FinancingV3CustomerExtraData from "@/module/main/dto/financing-v3/FinancingV3CustomerExtraData";
import IFinancingV3CustomerExtraData from "@/module/main/dto/financing-v3/IFinancingV3CustomerExtraData";

class CustomerMapperV3 extends CustomerBaseMapper
{
    public getCustomer(application: IModel): ICustomer
    {
        let person: IPerson | undefined = undefined;
        let entrepreneur: object | undefined = undefined;
        let legalEntity: object | undefined = undefined;
        let clientType: ClientType = application.loan.clientType;

        if (application.loan.financedSubjectType === FinancedSubjectType.ORDER
            && (application.loan.orderType === OrderType.BROKERED || application.loan.orderType === OrderType.CAR_LOAN))
        {
            clientType = ClientType.PERSON;
        }

        switch (clientType)
        {
            case ClientType.PERSON:
                person = this.getPerson(application.customer, application.currency.code);
                break;
            case ClientType.ENTREPRENEUR:
                entrepreneur = this.getCompany(application.customer.entrepreneur);
                break;
            case ClientType.LEGAL_ENTITY:
                legalEntity = this.getCompany(application.customer.entrepreneur);
                break;
        }

        return new Customer({
            type: clientType,
            entrepreneur,
            legalEntity,
            person
        });
    }

    private getPerson(customer: ICustomerModel, currencyCode: CurrencyCode): IPerson
    {
        return new Person({
            addresses: this.getAddresses(customer),
            bankAccount: this.getBankAccount(customer, currencyCode),
            birthplace: customer.block2Others.birthplace || undefined,
            childrenCount: this.stringToNumber(customer.block2Others.childrenCount) || undefined,
            consents: this.getConsents(customer),
            contacts: this.getContacts(customer.block1.phone, customer.block1.email) || undefined,
            education: EnumUtil.getEnumValue(Education, customer.block2Others.education),
            expenses: this.createAmount(customer.block2Income.expenses, currencyCode),
            extraData: this.createExtraData(customer),
            firstName: customer.block1.firstName || undefined,
            householdIncome: this.createAmount(customer.block2Income.householdIncome, currencyCode),
            housingTenure: EnumUtil.getEnumValue(HousingTenure, customer.block2Others.housingTenure),
            housingZipCode: customer.block2Others.housingZipCode || undefined,
            identificationNumber: customer.block1.identificationNumberRaw || undefined,
            income: this.getIncome(customer, currencyCode),
            lastName: customer.block1.lastName || undefined,
            cohabitation: EnumUtil.getEnumValue(Cohabitation, customer.block2Others.cohabitation),
            nationality: customer.block2Others.nationality || undefined,
            politicallyExposed: this.getPoliticallyExposed(customer),
            primaryPersonalDocument: this.getPrimaryPersonalDocument(customer),
            secondaryPersonalDocument: this.getSecondaryPersonalDocument(customer),
        });
    }

    private getCompany(company: IEntrepreneur): ICompany
    {
        let addresses: IAddressFlags[] = [];
        this.addAddress(addresses, company.address, [AddressFlagEnum.CONTACT]);

        const representative = new Person({
            firstName: company.representativeFirstName,
            lastName: company.representativeLastName
        });

        return new Company({
            businessIdentificationNumber: company.businessId || undefined,
            contacts: this.getContacts(company.phone, company.email) || undefined,
            addresses,
            representatives: [representative]
        });
    }

    private createExtraData(customer: ICustomerModel): IFinancingV3CustomerExtraData | undefined
    {
        let extraData = new FinancingV3CustomerExtraData({});
        let somethingAdded: boolean = false;

        if (!!customer.block1.platformUsed) {
            extraData.platformUsed = customer.block1.platformUsed;
            somethingAdded = true;
        }

        return somethingAdded ? extraData : undefined;
    }
}

export default new CustomerMapperV3();
